'use client';

import { memo, useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Code,
  FileText,
  Globe,
  Images,
  MusicNotes,
  SquaresFour,
  VideoCamera,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

type FeatureAction =
  | { type: 'route'; href: string }
  | { type: 'prefill'; prompt: string };

interface FeatureSlide {
  title: string;
  description: string;
  cta: string;
  icon: PhosphorIcon;
  action: FeatureAction;
  accentClass: string;
  preview: Array<{
    label: string;
    icon: PhosphorIcon;
    barClass: string;
    iconClass: string;
  }>;
}

const FEATURE_SLIDES: FeatureSlide[] = [
  {
    title: 'Continue Apps',
    description: 'Open apps built in sandboxes and continue development from where you left off.',
    cta: 'Open Apps',
    icon: SquaresFour,
    action: { type: 'route', href: '/apps' },
    accentClass: 'from-emerald-500/25 via-cyan-500/10 to-transparent',
    preview: [
      { label: 'Preview', icon: Globe, barClass: 'w-24 bg-emerald-300/35', iconClass: 'bg-emerald-500 text-white' },
      { label: 'Files', icon: Code, barClass: 'w-20 bg-cyan-300/30', iconClass: 'bg-cyan-500 text-white' },
      { label: 'Terminal', icon: Code, barClass: 'w-28 bg-violet-300/25', iconClass: 'bg-violet-500 text-white' },
    ],
  },
  {
    title: 'Build Websites',
    description: 'Create, run, preview, inspect files, and register finished app work.',
    cta: 'Start Build',
    icon: Code,
    action: { type: 'prefill', prompt: 'Build a polished web app for ' },
    accentClass: 'from-violet-500/25 via-blue-500/10 to-transparent',
    preview: [
      { label: 'React', icon: Code, barClass: 'w-24 bg-blue-300/35', iconClass: 'bg-blue-500 text-white' },
      { label: 'Preview', icon: Globe, barClass: 'w-32 bg-violet-300/30', iconClass: 'bg-violet-500 text-white' },
      { label: 'Tests', icon: FileText, barClass: 'w-20 bg-emerald-300/30', iconClass: 'bg-emerald-500 text-white' },
    ],
  },
  {
    title: 'Media Studio',
    description: 'Generate and inspect images, videos, music, voiceovers, and reusable brand assets.',
    cta: 'Open Media',
    icon: Images,
    action: { type: 'route', href: '/media' },
    accentClass: 'from-fuchsia-500/25 via-orange-500/10 to-transparent',
    preview: [
      { label: 'Images', icon: Images, barClass: 'w-24 bg-fuchsia-300/35', iconClass: 'bg-fuchsia-500 text-white' },
      { label: 'Video', icon: VideoCamera, barClass: 'w-28 bg-cyan-300/30', iconClass: 'bg-cyan-500 text-white' },
      { label: 'Music', icon: MusicNotes, barClass: 'w-20 bg-amber-300/30', iconClass: 'bg-amber-500 text-white' },
    ],
  },
  {
    title: 'Documents',
    description: 'Parse uploaded PDFs and files into readable workspace documents and briefs.',
    cta: 'Open Docs',
    icon: FileText,
    action: { type: 'route', href: '/documents' },
    accentClass: 'from-sky-500/25 via-indigo-500/10 to-transparent',
    preview: [
      { label: 'PDF', icon: FileText, barClass: 'w-20 bg-sky-300/35', iconClass: 'bg-sky-500 text-white' },
      { label: 'Summary', icon: FileText, barClass: 'w-32 bg-indigo-300/30', iconClass: 'bg-indigo-500 text-white' },
      { label: 'Brief', icon: FileText, barClass: 'w-24 bg-emerald-300/25', iconClass: 'bg-emerald-500 text-white' },
    ],
  },
  {
    title: 'Connected Apps',
    description: 'Connect Gmail, GitHub, Slack, Notion, Linear, and more so Galexia can take action.',
    cta: 'Connect Apps',
    icon: Globe,
    action: {
      type: 'prefill',
      prompt: 'Show me what apps I can connect, then help me set up the right tools for my workflow.',
    },
    accentClass: 'from-blue-500/25 via-emerald-500/10 to-transparent',
    preview: [
      { label: 'GitHub', icon: Code, barClass: 'w-28 bg-blue-300/35', iconClass: 'bg-blue-500 text-white' },
      { label: 'Notion', icon: FileText, barClass: 'w-20 bg-neutral-300/30', iconClass: 'bg-neutral-500 text-white' },
      { label: 'Slack', icon: Globe, barClass: 'w-24 bg-emerald-300/30', iconClass: 'bg-emerald-500 text-white' },
    ],
  },
];

const AUTO_ADVANCE_MS = 5200;

interface HomeFeatureCarouselProps {
  onPrefill: (prompt: string) => void;
  className?: string;
}

export const HomeFeatureCarousel = memo(function HomeFeatureCarousel({ onPrefill, className }: HomeFeatureCarouselProps) {
  const [index, setIndex] = useState(0);
  const reducedMotion = useReducedMotion();
  const router = useRouter();
  const slide = FEATURE_SLIDES[index];
  const Icon = slide.icon;

  useEffect(() => {
    if (reducedMotion) return;
    const id = window.setInterval(() => {
      setIndex((value) => (value + 1) % FEATURE_SLIDES.length);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [reducedMotion]);

  const activate = useCallback(() => {
    if (slide.action.type === 'route') {
      router.push(slide.action.href);
      return;
    }
    onPrefill(slide.action.prompt);
  }, [onPrefill, router, slide.action]);

  return (
    <section
      className={cn('w-full max-w-[360px]', className)}
      aria-label="Galexia feature carousel"
      aria-roledescription="carousel"
    >
      <button
        type="button"
        onClick={activate}
        className="group relative grid w-full overflow-hidden rounded-lg border border-white/[0.08] bg-[#222]/95 text-left shadow-[0_12px_36px_rgba(0,0,0,0.25)] outline-none transition-colors hover:border-white/[0.16] focus-visible:ring-2 focus-visible:ring-violet-400 sm:grid-cols-[1fr_92px]"
        aria-label={`${slide.title}: ${slide.cta}`}
      >
        <div className={cn('pointer-events-none absolute inset-0 bg-gradient-to-r opacity-100', slide.accentClass)} />
        <div className="relative min-w-0 p-3">
          <div className="mb-1.5 flex items-center gap-2 text-white">
            <span className="grid size-6 shrink-0 place-items-center rounded-md border border-white/10 bg-white/[0.06]">
              <Icon weight="duotone" className="size-3" />
            </span>
            <h2 className="truncate text-[13px] font-semibold tracking-normal">{slide.title}</h2>
          </div>
          <p className="line-clamp-2 max-w-[220px] text-[11px] leading-4 text-white/[0.58]">
            {slide.description}
          </p>
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-white/90">
            {slide.cta}
            <ArrowRight weight="bold" className="size-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>

        <div className="relative hidden border-l border-white/[0.08] bg-black/10 p-1.5 sm:block">
          <div className="space-y-1">
            {slide.preview.map((row) => {
              const RowIcon = row.icon;
              return (
                <div
                  key={row.label}
                  className="flex h-5 items-center gap-1.5 rounded-md border border-white/[0.08] bg-white/[0.07] px-1.5"
                >
                  <span className={cn('grid size-3.5 shrink-0 place-items-center rounded', row.iconClass)}>
                    <RowIcon weight="bold" className="size-2" />
                  </span>
                  <span className={cn('h-1 max-w-[46px] rounded-full', row.barClass)} />
                </div>
              );
            })}
          </div>
        </div>
      </button>

      <div className="mt-2 flex items-center justify-center gap-1.5" aria-label="Feature carousel pagination">
        {FEATURE_SLIDES.map((item, dotIndex) => {
          const active = dotIndex === index;
          return (
            <button
              key={item.title}
              type="button"
              onClick={() => setIndex(dotIndex)}
              className={cn(
                'h-1 rounded-full bg-white/[0.18] transition-all hover:bg-white/[0.45]',
                active ? 'w-4 bg-white/[0.38]' : 'w-1',
              )}
              aria-current={active ? 'true' : undefined}
              aria-label={`Show feature ${dotIndex + 1}: ${item.title}`}
            />
          );
        })}
      </div>
    </section>
  );
});
